// Module 10 — overlay-arc layer: renders information/finance FlowSamples, which ride SYNTHETIC
// `from→to` edge ids (the FROZEN layers.v1 id convention — U+2192 separator, orientation in the id,
// direction always +1). These arcs are NOT electrical edges, so the edge PolylineCollection can never
// match them — before this layer existed the Info/Finance toggles had nothing to show (the R11 gap).
// Discipline: a FIXED pool of polylines built once and repositioned/hidden in place — never per-tick
// add/remove (the documented Cesium re-batch cliff). Stride-sampling picks a deterministic, spatially
// spread subset when a tick carries more samples than the pool (~10k info samples vs an ~80 pool).
// Boundary: domain + same-module only (the id convention is frozen in layers.v1; parsed locally —
// importing the layers module here would widen viz's declared deps {domain, state}).
import type { FlowSample } from "../domain/index";
import type { CesiumLike, PolylineCollectionLike, PolylineLike, MaterialLike } from "./cesium-api";
import { ballisticArc } from "./arc-geometry";

const ARC_SEP = "→"; // "→" — frozen in layers.v1; absent from every Debug 3 node id

// Decode a synthetic overlay arc id. Returns null for real electrical edge ids (no separator).
export function arcEndpoints(edgeId: string): { from: string; to: string } | null {
  const i = edgeId.indexOf(ARC_SEP);
  if (i < 0) return null;
  return { from: edgeId.slice(0, i), to: edgeId.slice(i + ARC_SEP.length) };
}

export interface OverlayArcOpts {
  colorCss: string; // the ballistic LINE hue — bright blue (info) / bright indigo-blue (finance); the
  // moving transaction arrows ride a DIFFERENT colour (set on the glyph layer) so line ≠ arrows.
  apexScale: number; // ballistic arch height factor (apex = apexScale × node distance); finance > info,
  // so the two layers separate vertically AND the arcs lift clear off the flat electrical-edge plane.
  pool: number; // max simultaneous arcs (display budget, not a data cap)
  segments?: number; // arch smoothness (default from arc-geometry)
}

export interface OverlayArcLayer {
  apply(flows: readonly FlowSample[], posOf: (nodeId: string) => readonly [number, number] | undefined): void;
  hideAll(): void;
  // Phase 0.3: live tier knobs driven by applyQuality from the central TIER_KNOBS table.
  // setDensity(frac): scale the visible arc pool by `frac` (1.0 = full pool; 0.25 = quarter density).
  setDensity(frac: number): void;
  // setStaticMode(on): when true, collapse to a SINGLE static midpoint dot per arc (the LOD fallback
  // for the low tier — no marching, no arch, just a dim beacon that the flow exists). When false,
  // restore the full ballistic arch + marching glyph train (the default at every other tier).
  setStaticMode(on: boolean): void;
}

export function makeOverlayArcLayer(
  cesium: CesiumLike,
  lines: PolylineCollectionLike,
  opts: OverlayArcOpts,
): OverlayArcLayer {
  // PolylineGlow (Cesium built-in) → a LUMINOUS, tapered line — the glowing "global network" look of the
  // reference image, and the fix for arcs that "weren't illuminated". glowPower = fraction of the line
  // half-width that glows out from a crisp bright core; one material shared by every arc in this layer.
  const material: MaterialLike = new cesium.Material({
    fabric: {
      type: "PolylineGlow",
      uniforms: { color: cesium.Color.fromCssColorString(opts.colorCss).withAlpha(0.95), glowPower: 0.35, taperPower: 1.0 },
    },
  });
  const segments = opts.segments;
  const slots: PolylineLike[] = [];
  const keys: (string | null)[] = []; // per-slot arc id — skip the position write when unchanged

  // Phase 0.3: live tier knobs. densityFrac scales the visible pool; staticMode collapses arcs to a
  // single midpoint dot (the low-tier LOD fallback). Both default to full-quality.
  let densityFrac = 1.0;
  let staticMode = false;

  const ensure = (n: number): void => {
    while (slots.length < n) {
      slots.push(lines.add({ positions: [], width: 2, material, show: false, id: { kind: "arc" } }));
      keys.push(null);
    }
  };

  return {
    apply(flows, posOf): void {
      // The live pool is the construction pool scaled by the density tier knob (arcDensityFrac).
      const pool = Math.max(1, Math.round(opts.pool * densityFrac));
      // Deterministic spatial spread: stride over the sample list instead of sorting 10k entries
      // per tick (and instead of first-K, which would cluster every arc at the first few events).
      const stride = Math.max(1, Math.ceil(flows.length / pool));
      let slot = 0;
      for (let i = 0; i < flows.length && slot < pool; i += stride) {
        const f = flows[i]!;
        const ep = arcEndpoints(f.edgeId as string);
        if (!ep) continue; // a real electrical edge id — the edge collection owns it
        const a = posOf(ep.from);
        const b = posOf(ep.to);
        if (!a || !b) continue; // unknown endpoint — skip, never throw mid-render
        ensure(slot + 1);
        const line = slots[slot]!;
        if (keys[slot] !== (f.edgeId as string)) {
          if (staticMode) {
            // Phase 0.3 (arcsStatic): LOD fallback — a short 2-point stub at the midpoint height,
            // not the full ballistic arch. A dim beacon that the flow exists, at a fraction of the cost.
            const arc = ballisticArc(a, b, { apexScale: opts.apexScale, segments: 2 });
            const positions: unknown[] = [];
            for (let k = 0; k < arc.lonlat.length; k++) {
              positions.push(cesium.Cartesian3.fromDegrees(arc.lonlat[k]![0], arc.lonlat[k]![1], arc.heights[k]!));
            }
            line.positions = positions;
          } else {
            // Build the ballistic arch: many vertices following a sine height profile (0 at the nodes,
            // apex at mid) so the line springs off the ground and bulges high above the 2D edge plane.
            const arc = ballisticArc(a, b, segments === undefined ? { apexScale: opts.apexScale } : { apexScale: opts.apexScale, segments });
            const positions: unknown[] = [];
            for (let k = 0; k < arc.lonlat.length; k++) {
              positions.push(cesium.Cartesian3.fromDegrees(arc.lonlat[k]![0], arc.lonlat[k]![1], arc.heights[k]!));
            }
            line.positions = positions;
          }
          keys[slot] = f.edgeId as string;
          // carry the packet id so a click on this arc resolves to its from→to datasheet (picking.ts).
          // Idle (show:false) slots keep a stale id but Cesium never picks hidden primitives.
          (line as { id?: unknown }).id = { kind: "arc", id: f.edgeId as string };
        }
        // quantized width (0.25 steps) — same guard as edge widths: identical values skip the
        // setter's makeDirty re-batch. Wider than before (5–9 px) so the GLOW reads clearly at metro zoom.
        line.width = Math.round((5 + Math.min(Math.abs(f.magnitude), 50) / 12) * 4) / 4;
        line.show = true;
        slot++;
      }
      for (let i = slot; i < slots.length; i++) {
        slots[i]!.show = false;
        keys[i] = null;
      }
    },

    // Phase 0.3: live tier knobs — setDensity scales the visible pool; setStaticMode collapses to a
    // 2-segment stub (the low-tier LOD fallback). Both invalidate the per-slot key cache so the next
    // apply() rebuilds the geometry at the new setting.
    setDensity(frac: number): void {
      densityFrac = Math.max(0, Math.min(1, frac));
      for (let i = 0; i < keys.length; i++) keys[i] = null; // force geometry rebuild on next apply
    },
    setStaticMode(on: boolean): void {
      if (staticMode === on) return;
      staticMode = on;
      for (let i = 0; i < keys.length; i++) keys[i] = null; // force geometry rebuild on next apply
    },

    hideAll(): void {
      for (let i = 0; i < slots.length; i++) {
        slots[i]!.show = false;
        keys[i] = null;
      }
    },
  };
}
